/* =======================================================================
   Knowledge Base — the page. Azure item 416, phase 1.

   Ported from the knowledge-base mockup on 10 Sep 2026. The mockup's own
   fake app shell, follow-along drawer, Work Orders replica and walkthrough
   are not here: the shell is the real one, and the drawer and walkthrough
   are phases 2 and 3.

   URL model (all under /knowledge-base, registered as a splat route):
     /knowledge-base                 landing page — or, later in the same
                                     session, whatever was last open
     /knowledge-base/a/<slug>        an article
     /knowledge-base/m/<module>      a module's list
     /knowledge-base/s?q=<query>     search results

   Session memory (sessionStorage, mockup semantics kept exactly):
     makaniKbSeen         first visit this session shows the landing page;
                          every visit after that resumes the last route
     makaniKbLast         the route to resume
     makaniKbCollapsed    which tree sections the reader has opened
     makaniKbFlagsHidden  "hide open questions", kept across articles

   The content column is HTML from render.js, dropped in with
   dangerouslySetInnerHTML, and every link in it is handled by one
   delegated click handler below — the same design as the mockup, which
   is what lets the mockup stay a faithful preview of this page.
   ======================================================================= */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ARTICLES, MODULES, modLocked, byId, strip } from './articles';
import { renderLanding, renderArticle, renderModule, renderSearch, buildToc, highlight, withoutFlags } from './render';
import KbTree from './KbTree';
import KbSearchBox from './KbSearchBox';
import { KB_BASE as BASE, SESSION, readJson, writeJson, setLastArticle, announce, requestWalkthrough, syncRefsFromUrl, isLocalDev } from './kbSession';
import { handleFeedbackClick } from './kbFeedback';
import './kb.css';


/* Every section starts closed. The reader's own expand/collapse choices then
   persist for the rest of the session, including across trips elsewhere. */
const allCollapsed = () => MODULES.reduce((o, m) => { o[m] = true; return o; }, {});

export default function KnowledgeBase(){
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  /* ---- where we are, from the URL ---- */
  const splat = params['*'] || '';
  const route = useMemo(() => {
    const m = splat.match(/^(a|m|s)(?:\/(.*))?$/);
    if (!m) return { kind: 'home' };
    if (m[1] === 'a') return { kind: 'article', slug: decodeURIComponent(m[2] || '') };
    if (m[1] === 'm') return { kind: 'module', mod: decodeURIComponent(m[2] || '') };
    return { kind: 'search', q: (searchParams.get('q') || '').trim() };
  }, [splat, searchParams]);

  /* ---- state ---- */
  const [query, setQuery] = useState(route.kind === 'search' ? route.q : '');
  const [collapsed, setCollapsed] = useState(() => readJson('makaniKbCollapsed', allCollapsed()));
  const [flagsHidden, setFlagsHidden] = useState(() => SESSION.getItem('makaniKbFlagsHidden') === '1');
  const [toastMsg, setToastMsg] = useState('');
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const searchRef = useRef(null);
  const toastT = useRef(null);
  const debounceT = useRef(null);

  const toast = useCallback(msg => {
    setToastMsg(msg);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastMsg(''), 2600);
  }, []);

  const go = useCallback((to, opts) => navigate(to, opts), [navigate]);
  const openArticle = useCallback(a => {
    if (!a) return;
    if (a.stub){ toast('Not written yet — “' + a.title + '” is on the list.'); return; }
    setCollapsed(c => ({ ...c, [a.module]: false }));
    setQuery('');
    go(BASE + '/a/' + encodeURIComponent(a.slug));
  }, [go, toast]);
  const openModule = useCallback(mod => {
    setCollapsed(c => ({ ...c, [mod]: false }));
    setQuery('');
    go(BASE + '/m/' + encodeURIComponent(mod));
  }, [go]);
  const goHome = useCallback(() => { setQuery(''); go(BASE); }, [go]);

  const article = route.kind === 'article' ? byId(route.slug) : null;

  /* ?refs=1 turns our article tags on for this tab, ?refs=0 off. Read once a
     load, before the first render that could show them. */
  useEffect(() => { syncRefsFromUrl(); }, []);

  /* ---- first visit shows the landing page; later visits resume ---- */
  const booted = useRef(false);
  useEffect(() => {
    /* Once per mount, on purpose: later trips to the landing page (the logo,
       Home) must stay there, and the second effect below clears the resume
       route on each of those. Full deps keep the lint gate honest; the ref
       keeps the behaviour. */
    if (booted.current) return;
    booted.current = true;
    if (route.kind !== 'home') return;
    const seen = SESSION.getItem('makaniKbSeen');
    const last = SESSION.getItem('makaniKbLast');
    if (!seen){ SESSION.setItem('makaniKbSeen', '1'); return; }
    if (last && last !== BASE && location.state?.fresh !== true){
      go(last, { replace: true });
      toast('Picked up where you left off.');
    }
  }, [route.kind, location.state, go, toast]);

  /* remember the last thing open — never a search, which is transient */
  useEffect(() => {
    if (route.kind === 'article' && article && !article.stub){
      setLastArticle(article.slug);
      /* Opening an article is fresh intent: a drawer dismissed for the last job
         comes back for this one, even if it is the same article. */
      writeJson('makaniKbDrawer', { ...readJson('makaniKbDrawer', { width: 400 }), dismissed: false, hidden: false });
      announce();
    }
    else if (route.kind === 'module') SESSION.setItem('makaniKbLast', location.pathname);
    if (route.kind === 'home') SESSION.removeItem('makaniKbLast');
  }, [route, article, location.pathname]);

  useEffect(() => { SESSION.setItem('makaniKbCollapsed', JSON.stringify(collapsed)); }, [collapsed]);
  useEffect(() => { SESSION.setItem('makaniKbFlagsHidden', flagsHidden ? '1' : '0'); }, [flagsHidden]);

  /* ---- search: type, and the results column follows after a beat ---- */
  const submitSearch = useCallback(q => {
    const t = (q || '').trim();
    if (t.length >= 2) go(BASE + '/s?q=' + encodeURIComponent(t), { replace: route.kind === 'search' });
  }, [go, route.kind]);
  const onQueryChange = useCallback(v => {
    setQuery(v);
    clearTimeout(debounceT.current);
    if (v.trim().length >= 2) debounceT.current = setTimeout(() => submitSearch(v), 140);
  }, [submitSearch]);
  const clearSearch = useCallback(() => {
    clearTimeout(debounceT.current);
    setQuery('');
    if (route.kind === 'search') go(SESSION.getItem('makaniKbLast') || BASE, { replace: true });
  }, [go, route.kind]);

  /* ---- the content column ---- */
  const rendered = useMemo(() => {
    if (route.kind === 'search' && route.q.length >= 2) return renderSearch(route.q);
    if (route.kind === 'module' && MODULES.includes(route.mod)) return { html: renderModule(route.mod), stems: null };
    if (article && !article.stub) return { html: renderArticle(article, flagsHidden), stems: null };
    return { html: renderLanding(), stems: null };
  }, [route, article, flagsHidden]);

  /* After the HTML lands: table of contents, search highlighting, scroll to top. */
  useEffect(() => {
    const inner = innerRef.current, content = contentRef.current;
    if (!inner || !content) return;
    inner.querySelectorAll('aside.toc').forEach(t => t.remove());
    const toc = buildToc(inner);
    if (toc) inner.insertAdjacentHTML('beforeend', toc);
    if (rendered.stems) highlight(inner.querySelector('article'), rendered.stems);
    content.scrollTop = 0;
    spy();
  });

  /* On-this-page: highlight by *range* — each heading owns the space down
     to the next one, every section on screen is marked, and the topmost is
     the one you are reading. The last section could never light up under a
     "last heading scrolled past" rule, because you cannot scroll it to the
     top of the page. */
  const spy = useCallback(() => {
    const inner = innerRef.current, content = contentRef.current;
    if (!inner || !content) return;
    const links = [...inner.querySelectorAll('.toclink')];
    if (!links.length) return;
    const heads = links.map(l => inner.querySelector('#' + l.dataset.h));
    const view = content.getBoundingClientRect();
    const art = inner.querySelector('article');
    const artBottom = art ? art.getBoundingClientRect().bottom : view.bottom;
    let current = -1;
    links.forEach((l, i) => {
      const h = heads[i];
      if (!h){ l.classList.remove('active', 'current'); return; }
      const top = h.getBoundingClientRect().top;
      const bottom = heads[i + 1] ? heads[i + 1].getBoundingClientRect().top : artBottom;
      const on = bottom > view.top + 4 && top < view.bottom - 4;
      l.classList.toggle('active', on);
      if (on && current < 0) current = i;
    });
    if (current < 0){ current = 0; links[0].classList.add('active'); }
    links.forEach((l, i) => l.classList.toggle('current', i === current));
  }, []);

  /* ---- one delegated click handler for everything in the column ---- */
  const onContentClick = useCallback(e => {
    const t = e.target;
    if (!t.closest) return;

    const guide = t.closest('[data-guide]');    if (guide){ e.preventDefault(); requestWalkthrough(guide.dataset.guide); return; }
    const goEl = t.closest('[data-go]');
    if (goEl){ e.preventDefault(); goEl.dataset.go === 'home' ? goHome() : openArticle(byId(goEl.dataset.go)); return; }
    const modEl = t.closest('[data-mod-go]');
    if (modEl){ e.preventDefault(); openModule(modEl.dataset.modGo); return; }
    /* the feedback forms: article Yes/No, Send, No thanks (kbFeedback.js) */
    if (handleFeedbackClick(e, { article })) return;
    if (t.closest('[data-copy]')){
      if (article){
        const text = article.title + '\n\n' + strip(article.answer) + '\n\n' + strip(withoutFlags(article.body));
        if (navigator.clipboard && navigator.clipboard.writeText){
          navigator.clipboard.writeText(text).then(() => toast('Copied the article as plain text.'), () => toast('Could not copy — clipboard blocked.'));
        } else toast('Clipboard not available in this browser.');
      }
      return;
    }
    if (t.closest('[data-md]')){ toast('Markdown source is not available yet — articles are stored as HTML for now.'); return; }
    if (t.closest('#herosearch')){ searchRef.current && searchRef.current.focus(); return; }
    if (t.closest('[data-flagtoggle]')){
      e.preventDefault();
      setFlagsHidden(h => {
        toast(!h ? 'Open questions hidden — this is roughly how the article will read once they are answered.' : 'Open questions shown again.');
        return !h;
      });
      return;
    }
    const vh = t.closest('.verify-h');
    if (vh){
      e.preventDefault();
      const box = vh.closest('.verify');
      const nowCollapsed = box.classList.toggle('collapsed');
      vh.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
      return;
    }
    const tl = t.closest('.toclink');
    if (tl){
      const target = innerRef.current && innerRef.current.querySelector('#' + tl.dataset.h);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      innerRef.current.querySelectorAll('.toclink').forEach(x => x.classList.remove('current'));
      tl.classList.add('active', 'current');
      setTimeout(spy, 420);
    }
  }, [article, goHome, openArticle, openModule, spy, toast]);

  const onContentKey = useCallback(e => {
    const tl = e.target.closest && e.target.closest('.toclink');
    if (tl && (e.key === 'Enter' || e.key === ' ')){ e.preventDefault(); tl.click(); }
  }, []);

  /* ---- keyboard: "/" focuses search ---- */
  useEffect(() => {
    const onKey = e => {
      if (e.key === '/' && document.activeElement !== searchRef.current &&
          !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName)){
        e.preventDefault(); searchRef.current && searchRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ---- tree ---- */
  const onToggle = useCallback((mod, isSearch) => {
    /* mid-search toggling is a look, not a preference */
    if (isSearch) return;
    setCollapsed(c => ({ ...c, [mod]: !c[mod] }));
  }, []);

  return (
    <div className={'kb' + (flagsHidden || !isLocalDev() ? ' flags-hidden' : '')}>
      <div className="kbnav">
        <div className="kbsearch-wrap">
          <KbSearchBox
            value={query}
            inputRef={searchRef}
            onChange={onQueryChange}
            onOpen={openArticle}
            onSubmit={() => submitSearch(query)}
            onClear={clearSearch}
          />
          <div className="ctx-chip">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>
            <span>{ARTICLES.filter(a => !a.stub).length} articles across {MODULES.filter(m => !modLocked(m)).length} modules</span>
          </div>
        </div>
        <KbTree
          query={route.kind === 'search' ? route.q : query}
          activeSlug={article ? article.slug : null}
          collapsed={collapsed}
          onToggle={onToggle}
          onOpen={openArticle}
        />
      </div>

      <div className="content" ref={contentRef} onScroll={spy} onClick={onContentClick} onKeyDown={onContentKey}>
        <div className="content-inner" ref={innerRef} dangerouslySetInnerHTML={{ __html: rendered.html }} />
      </div>

      <div className={'kb-toast' + (toastMsg ? ' show' : '')} aria-live="polite">{toastMsg}</div>
    </div>
  );
}
